import {Label, RelativeTime} from '@primer/react'
import {ProfileReference} from '@github-ui/profile-reference'
import type {ElementType, ReactNode} from 'react'
import {StateType} from './types/list-types'
import styles from './PullsListItem.module.css'

export type AuthorFilterLinkProps = {
  as?: ElementType
  to?: string
  href?: string
  preload?: 'intent'
  reloadDocument?: boolean
  state?: unknown
}

interface PullsListItemTimestampContainerProps {
  createdAt: Date
  updatedAt: Date
  prState: StateType
  author: string
  isAgent?: boolean
  attributedUsername?: string
  onAuthorClick?: (author: string) => void
  getAuthorLinkProps?: (login: string) => AuthorFilterLinkProps | undefined
  showUpdatedAt?: boolean
}

export function AuthorFilterControl({
  login,
  testId,
  children,
  onAuthorClick,
  authorLinkProps,
}: {
  login: string
  testId: string
  children: ReactNode
  onAuthorClick?: (author: string) => void
  authorLinkProps?: AuthorFilterLinkProps
}) {
  const ariaLabel = `Filter by author ${login}`

  if (authorLinkProps) {
    const {as: Component = 'a', ...rest} = authorLinkProps
    return (
      <Component className={styles.filterLink} data-testid={testId} aria-label={ariaLabel} {...rest}>
        {children}
      </Component>
    )
  }

  if (onAuthorClick) {
    return (
      <button
        type="button"
        className={styles.filterLink}
        onClick={() => onAuthorClick(login)}
        data-testid={testId}
        aria-label={ariaLabel}
      >
        {children}
      </button>
    )
  }

  return children
}

export const PullsListItemTimestampContainer = ({
  prState,
  author,
  createdAt,
  updatedAt,
  isAgent = false,
  attributedUsername,
  onAuthorClick,
  getAuthorLinkProps,
  showUpdatedAt = true,
}: PullsListItemTimestampContainerProps) => {
  const hasBeenUpdated = createdAt.getTime() !== updatedAt.getTime()
  const isOpen = prState === StateType.Opened
  const isClosed = !isOpen

  const authorContent = <ProfileReference login={author} profileName={author} isAgent={isAgent} />

  return (
    <span data-testid="timestamp-container">
      <span className={styles.timestampSeparator} aria-hidden="true">
        &middot;
      </span>
      {attributedUsername && (
        <>
          <AuthorFilterControl
            login={attributedUsername}
            testId="attributed-author-filter-link"
            onAuthorClick={onAuthorClick}
            authorLinkProps={getAuthorLinkProps?.(attributedUsername)}
          >
            <ProfileReference login={attributedUsername} profileName={attributedUsername} isAgent={false} />
          </AuthorFilterControl>
          <span> with </span>
        </>
      )}
      <AuthorFilterControl
        login={author}
        testId="author-filter-link"
        onAuthorClick={onAuthorClick}
        authorLinkProps={getAuthorLinkProps?.(author)}
      >
        {authorContent}
      </AuthorFilterControl>
      {isAgent ? (
        <Label variant="secondary" className="ml-1">
          AI
        </Label>
      ) : null}
      <span> opened </span>
      <RelativeTime date={createdAt} />
      {showUpdatedAt && isOpen && hasBeenUpdated && (
        <span>
          {' '}
          <span>&middot;</span> <span>Updated</span> <RelativeTime date={updatedAt} />
        </span>
      )}
      {isClosed && (
        <span>
          {' '}
          <span>&middot;</span> <span>Closed</span> <RelativeTime date={updatedAt} />
        </span>
      )}
    </span>
  )
}
